import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@common-ui/ui";
import { Email } from "@mui/icons-material";
import { sendUserInvite } from "../../services";
import MandatoryIcon from "../../components/MandatoryIcon";

const UsersInvitationDialog = (props) => {
  const [loading, setLoading] = useState(false);
  const formik = useFormik({
    initialValues: {
      email: "",
      role: "",
      message: `You are invited to join ${props.orgName} organization.`,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email()
        .trim()
        .required("Email ID is required")
        .test({
          message: () => "Invalid domain",
          test: (value) => {
            if (value && props.restrictedDomainValues.length) {
              let splitArray = value.split("@");
              return props.restrictedDomainValues.indexOf(splitArray[1]) === -1
                ? false
                : true;
            } else {
              return true;
            }
          },
        }),
      role: Yup.string().trim().required("Role is required"),
      message: Yup.string().nullable(),
    }),
    onSubmit: async () => {
      setLoading(true);
      let { email, role, message } = formik.values;

      const attributes = {
        email: email,
        role: role,
        message: message,
      };

      try {
        await onSubmit(attributes, props.orgId);
        props.setToast([
          ...props.toast,
          {
            message: `${attributes.email} has been added.`,
            open: true,
            severity: "success",
          },
        ]);
        formik.resetForm();
        props.handleClose();
      } catch (error) {
        setLoading(false);
        props.setToast([
          ...props.toast,
          {
            message: error.message,
            open: true,
            severity: "error",
          },
        ]);
        formik.setSubmitting(false);
      }
    },
  });

  const onSubmit = async (attributes, orgId) => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const payload = await sendUserInvite(attributes, orgId);
        resolve({ payload: payload });
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    formik.resetForm();
    props.handleClose();
  };

  return (
    <div>
      <Dialog
        open={props.open}
        onClose={handleDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Add New User
          {props.open ? (
            <IconButton
              aria-label="close"
              onClick={handleDialogClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component={"span"} id="alert-dialog-description">
            <form
              onSubmit={formik.handleSubmit}
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={1}>
                  {/* Email Address */}
                  <Grid item>
                    <div style={{ padding: 1 }}>
                      <InputLabel
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Typography sx={{ m: 0 }} fontWeight="700">
                          Email
                        </Typography>

                        <MandatoryIcon />
                      </InputLabel>
                      <div style={{ display: "flex" }}>
                        <FormControl>
                          <TextField
                            style={{ width: 340 }}
                            sx={{ mt: 0 }}
                            size="small"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Email />
                                </InputAdornment>
                              ),
                            }}
                            autoComplete="off"
                            error={Boolean(
                              formik.touched.email && formik.errors.email
                            )}
                            helperText={
                              formik.touched.email && formik.errors.email
                            }
                            fullWidth
                            placeholder="Email"
                            margin="normal"
                            name="email"
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            type="email"
                            value={formik.values.email}
                            variant="outlined"
                          />
                        </FormControl>
                      </div>
                    </div>
                  </Grid>

                  {/* Roles */}
                  <Grid item>
                    <div>
                      <InputLabel sx={{ display: "flex" }}>
                        <Typography sx={{ m: 0 }} fontWeight="700">
                          Role
                        </Typography>
                        <MandatoryIcon />
                      </InputLabel>
                      <FormControl>
                        <Select
                          sx={{ width: 200 }}
                          size="small"
                          placeholder="Select"
                          fullWidth
                          name="role"
                          variant="outlined"
                          // helperText={formik.touched.role && formik.errors.role}
                          error={Boolean(
                            formik.touched.role && formik.errors.role
                          )}
                          value={formik.values.role}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        >
                          <MenuItem value="Role" disabled>
                            <em>Role</em>
                          </MenuItem>
                          <MenuItem value={"ADMIN"}>Admin</MenuItem>
                          <MenuItem value={"SUPERVISOR"}>Supervisor</MenuItem>
                          <MenuItem value={"TECHNICIAN"}>Technician</MenuItem>
                        </Select>
                        {formik.touched.role && formik.errors.role && (
                          <FormHelperText>Field is required</FormHelperText>
                        )}
                      </FormControl>
                    </div>
                  </Grid>

                  {/* Message to Invited Users */}
                  <Grid item xs={12} md={12}>
                    <div>
                      <InputLabel sx={{ display: "flex" }}>
                        <Typography sx={{ m: 0 }} fontWeight="700">
                          Message to Invited User
                        </Typography>
                      </InputLabel>
                      <TextField
                        fullWidth
                        // placeholder={}
                        multiline
                        rows={2}
                        name="message"
                        onChange={formik.handleChange}
                        value={formik.values.message}
                      />
                    </div>
                  </Grid>
                </Grid>
              </Box>
            </form>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} variant="contained">
            Cancel
          </Button>
          <Button
            color="secondary"
            disabled={!(formik.isValid && formik.dirty) && !loading}
            variant="contained"
            type="submit"
            onClick={formik.handleSubmit}
          >
            {loading && <CircularProgress size={14} />}
            {!loading && "Send Invite"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UsersInvitationDialog;
